import ApiService from '@/core/services/api.service';
import WebhookService from '@/core/services/webhook.service';

const api = new ApiService();
const webhook = new WebhookService();

export default {
  // Get paginated list of decision records
  async getList(params) {
    return webhook.get('/DecisionAiCoach', { params });
  },

  // Get decision record by ID
  async getById(id) {
    return webhook.get(`/DecisionAiCoach/${id}`);
  },

  // Get stats
  async getStats(params) {
    return webhook.get('/DecisionAiCoach/stats', { params });
  },

  // Get approval stats
  async getApprovalStats(params) {
    return webhook.get('/DecisionAiCoach/approval-stats', { params });
  },

  // Mark as viewed
  async markAsViewed(id) {
    return webhook.post(`/DecisionAiCoach/${id}/viewed`);
  },

  // Send/approve message
  async send(payload) {
    return webhook.post('/DecisionAiCoach/send', payload);
  },

  // Reject message
  async reject(payload) {
    return webhook.post('/DecisionAiCoach/reject', payload);
  },

  // Bỏ qua - không phản hồi khách hàng, chỉ đổi trạng thái record
  async skip(id) {
    return webhook.post(`/DecisionAiCoach/skip/${id}`);
  },

  // Danh sách coach có thể chọn làm văn phong mẫu, kèm số lượt đã duyệt.
  // Trả về cả globalStyleCoachId đang set chung cho toàn hệ thống.
  async getStyleCoaches() {
    return webhook.get('/DecisionAiCoach/style-coaches');
  },

  // Đặt văn phong riêng cho 1 nhóm. coachId rỗng = nhóm quay về cấu hình chung.
  // POST kèm body: axios ở đây ép Content-Type json cho mọi request, PUT không body bị 415.
  async setGroupStyleCoach(groupId, coachId) {
    return webhook.post('/DecisionAiCoach/style-coach/group', {
      groupId,
      coachId: coachId || null,
    });
  },

  // Văn phong few-shot theo chủ đề: { topics: [{key,label,coaches:[{coachId,name,samples}]}], groupStyles, minSamples }
  async getStyleCoachesByTopic() {
    return webhook.get('/DecisionAiCoach/style-coaches-by-topic');
  },

  // Đặt coach mẫu cho 1 chủ đề của nhóm. coachId rỗng = không gửi few-shot, AI tự viết.
  // pairIds rỗng = lấy 5 câu mới nhất của coach
  async setGroupTopicStyleCoach(groupId, kpiType, coachId, pairIds = []) {
    return webhook.post('/DecisionAiCoach/style-coach/group-topic', {
      groupId,
      kpiType,
      coachId: coachId || null,
      pairIds,
    });
  },

  // Câu mẫu đã duyệt của 1 coach ở 1 chủ đề, mới nhất trước
  async getStyleSamples(coachId, kpiType, take = 50) {
    return webhook.get('/DecisionAiCoach/style-samples', { params: { coachId, kpiType, take } });
  },

  // Get AI enabled groups (turn on/off AI for group - default off)
  async getAIEnabledGroups() {
    return webhook.get('/DecisionAiCoach/ai-enabled-groups');
  },

  // Save AI enabled groups (turn on/off AI for group - default off)
  async saveAIEnabledGroups(groupIds) {
    return webhook.post('/DecisionAiCoach/ai-enabled-groups', groupIds);
  },

  // Get customer receives for coach mapping
  // Dùng /All (không phân trang, không enrich từng dòng): bản cũ size 1000 sót nhóm khi khách vượt 1000.
  // /All trả mảng thô nên bọc lại thành { data } cho khớp chỗ gọi.
  async getCustomerReceives(params = { status: 1, isDeleted: false }) {
    const res = await api.get('CustomerReceives/All', { params });
    return { data: Array.isArray(res) ? res : res?.data || [] };
  },

  // Get coaches in course
  // Khung giờ coach nhận nhắc tin chờ duyệt: { coachId: [7 chuỗi T2..CN x 48 ô 0/1] }
  async getCoachActiveHours() {
    return webhook.get('/DecisionAiCoach/coach-active-hours');
  },

  // Hiệu suất phản hồi: từ lúc AI gợi ý tới lúc coach xử lý, N ngày gần nhất
  // types: ['ai', 'noai'] - AI gợi ý / AI không gợi ý
  // range: { days } hoặc { from, to } dạng 'YYYY-MM-DD' theo giờ VN
  async getCoachResponseStats(coachId, range = { days: 30 }, types = ['ai']) {
    return webhook.get('/DecisionAiCoach/coach-response-stats', { params: { coachId, ...range, types: types.join(',') } });
  },

  async saveCoachActiveHours(coachId, days) {
    return webhook.post('/DecisionAiCoach/coach-active-hours', { coachId, days });
  },

  async getCoachesInCourse(courseId) {
    return api.get(`Courses/CoachInCourse?courseId=${courseId}`);
  },

  // Get valid group OA
  // { groupId: số customer_receives gán vào nhóm } - không có key = nhóm chưa được chỉ định
  async getGroupCustomerCounts() {
    return webhook.get('/DecisionAiCoach/group-customer-counts');
  },

  // Người (không phải coach/OA) đã nhắn trong nhóm, kèm người AI đang theo dõi (isSelected)
  async getGroupAiCandidates(groupId) {
    return webhook.get(`/DecisionAiCoach/group-ai-candidates/${groupId}`);
  },

  // Số người (không tính coach/OA) đã nhắn theo nhóm: { groupId: count }
  async getGroupAiCandidateCounts(groupIds) {
    return webhook.post('/DecisionAiCoach/group-ai-candidate-counts', groupIds);
  },

  // Đặt người AI theo dõi cho nhóm; zaloUserId rỗng = theo dõi tất cả
  async setGroupAiTarget(groupId, zaloUserId) {
    return webhook.post('/DecisionAiCoach/group-ai-target', { groupId, zaloUserId: zaloUserId || null });
  },

  async getValidGroupOA(params) {
    return api.get('/ZaloZnsLog/GetValidGroupOA', { params });
  },

  // Get Zalo chat history for a group/recipient
  async getZaloChatHistory(accountId) {
    return webhook.get(`/ZaloWebHook/ChatGroup/${accountId}`, { params: { export: true } });
  },

  async sendDirectMessage(id, message) {
    // Chat only - does not update EditedMessage/Status
    return webhook.post('/DecisionAiCoach/SendChatOnly', { id, message });
  },

  async uploadMedia(file) {
    const formData = new FormData();
    formData.append('file', file);
    const res = await webhook.postForm('/DecisionAiCoach/UploadAttachment', formData);
    return res?.data || res;
  },

  // message: chu coach nhap kem file, BE gop chung mot tin nhan Zalo
  async sendMedia(id, mediaUrl, mediaType, message = '') {
    return webhook.post('/DecisionAiCoach/SendMedia', { id, mediaUrl, mediaType, message });
  },

  async shareItem(id, toUserIds, content = '') {
    return webhook.post(`DecisionAiCoach/Items/${id}/Share`, { itemId: id, toUserIds, content });
  },

  // Chỉ số AI trích ra của 1 tin, tách từng dòng. data = null khi tin không có chỉ số
  async getKpi(id) {
    return webhook.get(`/DecisionAiCoach/${id}/kpi`);
  },

  // Coach lưu các dòng đã tick. Tách khỏi luồng gửi: không nhắn gì cho khách hàng
  // payload: { glucoseRows: [...] } hoặc { nutrition: {..., items: [món đã tick]} }
  async saveKpi(id, payload) {
    return webhook.post(`/DecisionAiCoach/${id}/save-kpi`, payload);
  },

  // Chỉ ghi bản coach sửa vào tin, chưa lưu sang app
  async updateKpi(id, payload) {
    return webhook.post(`/DecisionAiCoach/${id}/update-kpi`, payload);
  },

  // Xoá chỉ số AI nhận diện sai khỏi tin (không xoá được khi đã lưu)
  async deleteKpi(id) {
    return webhook.post(`/DecisionAiCoach/${id}/delete-kpi`, {});
  },

  // Cột trái modal Cập nhật chỉ số: khách hàng có chỉ số, kèm số chờ lưu (tính cả tin đã duyệt)
  // params: { coachId, days, pendingOnly }
  async getKpiPatients(params) {
    return webhook.get('/DecisionAiCoach/kpi-patients', { params });
  },

  // Modal tổng quan: mọi tin có chỉ số của CÙNG khách hàng với tin decisionId, trong N ngày.
  // Neo theo tin: BE tự lấy patient + account của tin đó để lọc, không nhận patientId từ FE
  async getPatientKpiOverview(decisionId, days = 30) {
    return webhook.get(`/DecisionAiCoach/${decisionId}/kpi-overview`, { params: { days } });
  },

  // Coach bỏ qua chỉ số AI nhận diện, không lưu
  async skipKpi(id) {
    return webhook.post(`/DecisionAiCoach/${id}/skip-kpi`, {});
  },

  // Session management
  async getSessions(groupId, limit = 20) {
    return webhook.get(`/DecisionAiCoach/sessions/${groupId}`, { params: { limit } });
  },

  async getSessionMessages(sessionId) {
    return webhook.get(`/DecisionAiCoach/session/${sessionId}/messages`);
  },
};
